import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  TituloDeSecao,
} from '../componentes/base';
import { BotaoCopiar } from '../componentes/BotaoCopiar';
import { Caixa } from '../componentes/campos';
import { ModalDeClinica } from '../componentes/FormularioDeClinica';
import { VigenciaDoContrato, type Vigencia } from '../componentes/VigenciaDoContrato';
import { ModalJuntarClinicas } from '../componentes/JuntarClinicas';
import { dataHora, moeda, percentual, plural } from '../util/formato';

interface Clinica {
  readonly id: string;
  readonly nome: string;
  readonly cnpj: string | null;
  readonly responsavel: string | null;
  readonly telefone: string | null;
  readonly email: string | null;
  readonly chavePix: string | null;
  readonly percentualHonorarios: number;
  readonly mensalidade: number;
  readonly recebidoAnterior: number;
  readonly honorariosAnterior: number;
  readonly diaRepasse: number;
  readonly adiantaCustas: boolean;
  readonly fasesDeAcompanhamento: boolean;
  readonly contrato: Vigencia;
  readonly ativo: boolean;
  readonly devedoresAtivos: number;
  readonly carteira: number;
  readonly recuperado: number;
  readonly portal: {
    token: string;
    titular: string;
    ativo: boolean;
    verFinanceiro: boolean;
    ultimoAcesso: string | null;
    /** As OUTRAS clínicas que abrem pelo mesmo link. */
    compartilhadoCom: Array<{ id: string; nome: string }>;
  } | null;
}

export function PaginaClinicas() {
  const clienteConsulta = useQueryClient();
  const [cadastrando, setCadastrando] = useState(false);
  /** Clínica aberta no diálogo de alteração. `null` significa "nenhuma". */
  const [editando, setEditando] = useState<Clinica | null>(null);
  const [verDesativadas, setVerDesativadas] = useState(false);
  /** Clínica cujo LINK sobrevive ao juntar. `null` fecha o diálogo. */
  const [juntandoEm, setJuntandoEm] = useState<Clinica | null>(null);
  const [acesso, setAcesso] = useState<{ nome: string; link: string; senha: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const { data: clinicas, isLoading } = useQuery({
    queryKey: ['clinicas', verDesativadas],
    queryFn: () =>
      api.buscar<Clinica[]>('/clinicas', verDesativadas ? { inativas: '1' } : undefined),
  });

  /**
   * Desativar e reativar.
   *
   * Clínica com carteira NÃO se exclui — apagar levaria junto acordos, pagamentos e
   * fechamento já apurado. Desativar tira dos seletores e do dia a dia e preserva o
   * histórico, que é o que a prestação de contas exige anos depois.
   */
  const alternarAtivo = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) =>
      api.alterar(`/clinicas/${id}`, { ativo }),
    onSuccess: () => {
      setErro(null);
      void clienteConsulta.invalidateQueries({ queryKey: ['clinicas'] });
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível alterar a clínica.'),
  });

  const emitir = useMutation({
    mutationFn: (id: string) =>
      api.criar<{ token: string; senha: string; clinica: { nome: string } }>(
        `/clinicas/${id}/portal`,
        {},
      ),
    onSuccess: (r) => {
      setErro(null);
      setAcesso({
        nome: r.clinica.nome,
        link: `${window.location.origin}/portal/${r.token}`,
        senha: r.senha,
      });
      void clienteConsulta.invalidateQueries({ queryKey: ['clinicas'] });
    },
    onError: (e) => setErro(e instanceof FalhaDaApi ? e.message : 'Falha ao emitir o acesso.'),
  });

  const desagrupar = useMutation({
    mutationFn: (id: string) => api.remover(`/clinicas/${id}/portal/agrupar`),
    onSuccess: () => {
      setErro(null);
      void clienteConsulta.invalidateQueries({ queryKey: ['clinicas'] });
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível separar as clínicas.'),
  });

  const alternarFinanceiro = useMutation({
    mutationFn: ({ id, ver }: { id: string; ver: boolean }) =>
      api.substituir(`/clinicas/${id}/portal/financeiro`, { verFinanceiro: ver }),
    onSuccess: () => void clienteConsulta.invalidateQueries({ queryKey: ['clinicas'] }),
    onError: (e) =>
      setErro(
        e instanceof FalhaDaApi
          ? e.message
          : 'Não foi possível mudar o que a clínica vê no portal.',
      ),
  });

  if (isLoading || !clinicas) return <Carregando />;

  return (
    <>
      <CabecalhoDaPagina
        titulo="Clínicas e portal"
        descricao="Contratantes, condições comerciais e acesso ao portal"
        acoes={
          <>
            <Caixa
              rotulo="Mostrar desativadas"
              marcado={verDesativadas}
              aoMudar={setVerDesativadas}
            />
            <Botao tipo="primario" onClick={() => setCadastrando(true)}>
              Nova clínica
            </Botao>
          </>
        }
      />

      {/* Um diálogo para cada papel, em vez de um só com a clínica ora nula: assim
          fechar a alteração não pisca o formulário vazio do cadastro. */}
      <ModalDeClinica aberto={cadastrando} aoFechar={() => setCadastrando(false)} />
      <ModalDeClinica
        aberto={editando !== null}
        aoFechar={() => setEditando(null)}
        clinica={editando ?? undefined}
      />
      <ModalJuntarClinicas
        aberto={juntandoEm !== null}
        aoFechar={() => setJuntandoEm(null)}
        destino={juntandoEm}
        clinicas={clinicas}
      />

      {erro && (
        <div className="mb-5">
          <Alerta tom="vencido">{erro}</Alerta>
        </div>
      )}

      {/* A senha aparece UMA ÚNICA VEZ. Depois disso só existe o hash — e a tela
          precisa deixar isso explícito, senão alguém fecha o aviso e perde o acesso. */}
      {acesso && (
        <Cartao className="mb-6" >
          <TituloDeSecao auxiliar="anote agora — a senha não é exibida de novo">
            Acesso emitido para {acesso.nome}
          </TituloDeSecao>
          <dl className="flex flex-col gap-3 text-sm">
            <div>
              <dt className="text-xs text-[color:var(--texto-tenue)]">Link exclusivo</dt>
              <dd className="mt-0.5 flex items-start gap-2">
                <span className="font-mono text-xs break-all text-[color:var(--texto)]">
                  {acesso.link}
                </span>
                <BotaoCopiar texto={acesso.link} rotulo="Copiar" />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--texto-tenue)]">Senha</dt>
              <dd className="mt-0.5 flex items-center gap-3">
                <span
                  className="font-mono text-lg font-medium"
                  style={{ color: 'var(--acento)' }}
                >
                  {acesso.senha}
                </span>
                <BotaoCopiar texto={acesso.senha} rotulo="Copiar" />
              </dd>
            </div>
          </dl>

          {/* A senha some daqui e não volta; o link, sim — fica no cartão da
              clínica. Dizer isso evita a anotação apressada e o reenvio que
              derrubaria a recepção que já está usando o portal. */}
          <p className="mt-4 text-xs text-[color:var(--texto-suave)]">
            Guarde a <strong>senha</strong> agora — ela não é exibida outra vez. O
            <strong> link</strong> você reencontra a qualquer momento no cartão da clínica,
            logo abaixo.
          </p>

          <div className="mt-4">
            <BotaoCopiar
              texto={`Portal da ${acesso.nome} — Bianca Ávila Advocacia\n\nAcesse: ${acesso.link}\nSenha: ${acesso.senha}\n\nO link é exclusivo da sua clínica. Guarde nos favoritos.`}
              rotulo="Copiar mensagem pronta"
            />
          </div>

          <div className="mt-4">
            <Botao onClick={() => setAcesso(null)}>Já anotei</Botao>
          </div>
        </Cartao>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {clinicas.map((c) => (
          <Cartao key={c.id}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg text-[color:var(--texto)]">{c.nome}</h2>
                {c.responsavel && (
                  <p className="text-xs text-[color:var(--texto-tenue)]">{c.responsavel}</p>
                )}
              </div>
              {!c.ativo && <Ficha tom="neutro">Desativada</Ficha>}
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <dt className="text-xs text-[color:var(--texto-tenue)]">Percentual de êxito</dt>
                <dd className="tabular font-semibold" style={{ color: 'var(--acento)' }}>
                  {percentual(c.percentualHonorarios)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[color:var(--texto-tenue)]">Mensalidade</dt>
                <dd className="tabular">{moeda(c.mensalidade)}</dd>
              </div>
              <div>
                <dt className="text-xs text-[color:var(--texto-tenue)]">Contrato</dt>
                <dd className="text-sm"><VigenciaDoContrato contrato={c.contrato} /></dd>
              </div>
              <div>
                <dt className="text-xs text-[color:var(--texto-tenue)]">Carteira</dt>
                <dd className="tabular">
                  {moeda(c.carteira)}
                  <span className="ml-1 text-xs text-[color:var(--texto-tenue)]">
                    · {plural(c.devedoresAtivos, 'devedor', 'devedores')}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[color:var(--texto-tenue)]">Já recuperado</dt>
                <dd className="tabular" style={{ color: 'var(--color-recebido)' }}>
                  {moeda(c.recuperado)}
                </dd>
              </div>
            </dl>

            <p className="mt-3 text-xs text-[color:var(--texto-suave)]">
              Repasse todo dia {c.diaRepasse}
              {c.adiantaCustas
                ? ' · o escritório adianta as custas e é reembolsado'
                : ' · as custas ficam por conta da clínica'}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
              {c.portal?.ativo ? (
                <>
                  <Ficha tom="recebido">Portal ativo</Ficha>
                  {c.portal.ultimoAcesso && (
                    <span className="text-xs text-[color:var(--texto-tenue)]">
                      último acesso {dataHora(c.portal.ultimoAcesso)}
                    </span>
                  )}
                </>
              ) : (
                <Ficha tom="neutro">Portal não emitido</Ficha>
              )}
            </div>

            {/* Um acesso que abre duas carteiras precisa dizer isso aqui — sem o
                aviso, revogar "uma" clínica derrubaria a outra junto e a surpresa
                apareceria com o dentista sem conseguir entrar.

                Em tom NEUTRO, de propósito: dividir acesso é um estado normal, não
                um problema. Laranja de alerta aqui faria a colaboradora procurar o
                que consertar. */}
            {c.portal?.ativo && c.portal.compartilhadoCom.length > 0 && (
              <div className="mt-3 rounded-md bg-[color:var(--superficie-afundada)] px-3 py-2.5 text-xs">
                <p className="text-[color:var(--texto)]">
                  Acesso compartilhado — <strong>{c.portal.titular}</strong>
                </p>
                <p className="mt-1 text-[color:var(--texto-suave)]">
                  Um link e uma senha abrem esta carteira e{' '}
                  {c.portal.compartilhadoCom.map((o) => o.nome).join(', ')}.
                </p>
              </div>
            )}

            {/* O link, sempre à mão. É ele que a clínica abre, e é o que se
                reenvia quando a recepção o perde — sem trocar a senha, que
                derrubaria quem já está dentro. */}
            {c.portal?.ativo && (
              <div className="mt-3 flex items-start gap-2 rounded-md border bg-[color:var(--superficie-afundada)] px-3 py-2">
                <span className="min-w-0 flex-1 font-mono text-[0.6875rem] break-all text-[color:var(--texto-suave)]">
                  {linkDoPortal(c.portal.token)}
                </span>
                <BotaoCopiar texto={linkDoPortal(c.portal.token)} rotulo="Copiar link" />
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <Botao
                tipo="primario"
                onClick={() => setEditando(c)}
                className="px-2.5 py-1 text-xs"
              >
                Editar
              </Botao>
              <Botao
                onClick={() => emitir.mutate(c.id)}
                desabilitado={emitir.isPending}
                className="px-2.5 py-1 text-xs"
              >
                {c.portal?.ativo ? 'Gerar nova senha' : 'Emitir acesso ao portal'}
              </Botao>
              {c.portal?.ativo && (
                <Botao
                  onClick={() =>
                    alternarFinanceiro.mutate({ id: c.id, ver: !c.portal!.verFinanceiro })
                  }
                  className="px-2.5 py-1 text-xs"
                >
                  {c.portal.verFinanceiro
                    ? 'Ocultar valores no portal'
                    : 'Mostrar valores no portal'}
                </Botao>
              )}
              <Link
                to={`/devedores?clinicaId=${c.id}`}
                className="rounded-md border px-2.5 py-1 text-xs text-[color:var(--texto)] hover:border-[color:var(--acento)] hover:text-[color:var(--acento)]"
              >
                Ver carteira
              </Link>
              {c.portal?.ativo && (
                <Botao onClick={() => setJuntandoEm(c)} className="px-2.5 py-1 text-xs">
                  Juntar outra clínica
                </Botao>
              )}
              {c.portal?.ativo && c.portal.compartilhadoCom.length > 0 && (
                <Botao
                  tipo="discreto"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Tirar ${c.nome} deste acesso compartilhado?\n\n` +
                          'Ela fica SEM portal — emitir um acesso novo é um passo à parte.',
                      )
                    ) {
                      desagrupar.mutate(c.id);
                    }
                  }}
                  desabilitado={desagrupar.isPending}
                  className="px-2.5 py-1 text-xs"
                >
                  Separar
                </Botao>
              )}
              <Botao
                tipo="discreto"
                onClick={() => {
                  const pergunta = c.ativo
                    ? `Desativar ${c.nome}? A carteira e o histórico permanecem, mas ela sai dos seletores do sistema.`
                    : `Reativar ${c.nome}?`;
                  if (window.confirm(pergunta)) {
                    alternarAtivo.mutate({ id: c.id, ativo: !c.ativo });
                  }
                }}
                desabilitado={alternarAtivo.isPending}
                className="px-2.5 py-1 text-xs"
              >
                {c.ativo ? 'Desativar' : 'Reativar'}
              </Botao>
            </div>
          </Cartao>
        ))}
      </div>

      {/* Enviar um link `localhost` para a clínica é o erro óbvio de quem está
          testando; o endereço é montado a partir da página, então aqui ele sai local. */}
      {ambienteLocal() && (
        <div className="mt-5">
          <Alerta tom="atencao">
            Você está no ambiente de testes: os links acima apontam para esta máquina e não
            funcionam fora dela. Para enviar às clínicas, gere o link com o sistema aberto
            no endereço de produção.
          </Alerta>
        </div>
      )}

      <p className="mt-5 text-xs text-[color:var(--texto-tenue)]">
        Os honorários do escritório nunca aparecem no portal, independentemente da opção de
        mostrar valores — a clínica vê o que foi recuperado e o que ficou com ela.
      </p>
    </>
  );
}

/**
 * O endereço que a clínica vai abrir.
 *
 * Montado a partir da origem da página, e não de um domínio escrito no código: em
 * produção sai `https://cobrajus.tech/portal/…`, e em desenvolvimento sai o
 * endereço local — que é justamente o que a nota abaixo avisa não enviar.
 */
function linkDoPortal(token: string): string {
  return `${window.location.origin}/portal/${token}`;
}

function ambienteLocal(): boolean {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local');
}
